import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface UIState {
  // Query Editor panels
  showAIAssistant: boolean
  showQueryLibrary: boolean
  showQueryHistory: boolean
  rightSidebarWidth: number
}

interface UIStore {
  ui: UIState
  updateAvailable: boolean

  // Actions
  setShowAIAssistant: (show: boolean) => void
  setShowQueryLibrary: (show: boolean) => void
  setShowQueryHistory: (show: boolean) => void
  setRightSidebarWidth: (width: number) => void
  toggleAIAssistant: () => void
  toggleQueryLibrary: () => void
  toggleQueryHistory: () => void
  closeAllPanels: () => void
  setUpdateAvailable: (available: boolean) => void

  // Helper to check if any panel is open
  hasOpenPanel: () => boolean
}

const defaultUIState: UIState = {
  showAIAssistant: false,
  // Default to true as per existing behavior
  showQueryLibrary: true,
  showQueryHistory: false,
  // Default width percentage
  rightSidebarWidth: 33,
}

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ui: defaultUIState,
      updateAvailable: false,

      setUpdateAvailable: (available: boolean) => {
        set({ updateAvailable: available })
      },

      setShowAIAssistant: (show: boolean) => {
        set((state) => ({
          ui: {
            ...state.ui,
            showAIAssistant: show,
            // Close other panels when opening AI Assistant
            ...(show && {
              showQueryLibrary: false,
              showQueryHistory: false,
            }),
          },
        }))
      },

      setShowQueryLibrary: (show: boolean) => {
        set((state) => ({
          ui: {
            ...state.ui,
            showQueryLibrary: show,
            // Close other panels when opening Query Library
            ...(show && {
              showAIAssistant: false,
              showQueryHistory: false,
            }),
          },
        }))
      },

      setShowQueryHistory: (show: boolean) => {
        set((state) => ({
          ui: {
            ...state.ui,
            showQueryHistory: show,
            // Close other panels when opening Query History
            ...(show && {
              showAIAssistant: false,
              showQueryLibrary: false,
            }),
          },
        }))
      },

      setRightSidebarWidth: (width: number) => {
        set((state) => ({
          ui: {
            ...state.ui,
            // Restored original limits since Allotment handles max size
            rightSidebarWidth: Math.max(5, Math.min(80, width)),
          },
        }))
      },

      toggleAIAssistant: () => {
        const { ui } = get()
        get().setShowAIAssistant(!ui.showAIAssistant)
      },

      toggleQueryLibrary: () => {
        const { ui } = get()
        get().setShowQueryLibrary(!ui.showQueryLibrary)
      },

      toggleQueryHistory: () => {
        const { ui } = get()
        get().setShowQueryHistory(!ui.showQueryHistory)
      },

      closeAllPanels: () => {
        set((state) => ({
          ui: {
            ...state.ui,
            showAIAssistant: false,
            showQueryLibrary: false,
            showQueryHistory: false,
          },
        }))
      },

      hasOpenPanel: () => {
        const { ui } = get()
        return ui.showAIAssistant || ui.showQueryLibrary || ui.showQueryHistory
      },
    }),
    {
      name: 'ui-state-storage',
      partialize: (state) => ({
        ui: state.ui,
      }),
    }
  )
)